/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import {
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';
import { LuiToolbar, LuiEmptyState } from '@jda/lui-common-component-library-mui5';

import AddMachineDialog from '../../components/AddMachineDialog';
import { getAllClients } from '../../services/client';
import { useStyles } from './styles';
import { Box, Button } from '@mui/material';
import { IClient } from '../../models/Machines/ClientContracts';
import MachineList from '../../components/MachineList';
import EmptyComponent from '../../shared/EmptyComponent/EmptyComponent';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import { deleteClient as deleteClientService } from '../../services/client';
import {
  DIALOGUE_OK,
  DIALOGUE_CANCEL,
  DIALOGUE_CONTENT,
  EMPTY_SCREEN_TEXT,
  EMPTY_SCREEN_TITLE
} from '../../components/MachineList/Constants';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { NO_RESULT_FOUND, PLEASE_TRY_A_DIFFERENT_MACHINE, DELETE_MACHINE_TITLE } from '../constants';
import { CheckViewPermission } from '../../shared/CheckPermissions/CheckViewPermission';
import { permissions } from '../../shared/CheckPermissions/constants';
import { selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import { SEARCH_MACHINE, SNACKBAR_ALERT_DEFAULT_PROPS, GET_MACHINE_ERROR, ADD_MACHINE } from './constants';
import interpolateString from '../../utility/stringInterpolation';

import { useDispatch, useSelector } from 'react-redux';
import CustomSearch from '../../components/CustomSearch';
import {
  selectMachineCurrentName,
  selectMachineCurrentSelection,
  selectMachineSearch
} from '../../store/machine/machineSelector';
import { selectRealmInfo } from '../../store/realmSettings/realmSettingsSelector';
import {
  setCurrentMachineName,
  setCurrentMachineSelection,
  setMachineSearchValue,
  setMachineSelectedTab
} from '../../store/machine/machineActionCreator';
import { setSnackBarMessage, hideSnackbar } from '../../store/snackBar/snackBarActionCreator';
import { getUserTokenRealmId, isPortalV2 } from '../../utility/commonUtility';
import { getAllowedRealms } from '../../services/client';
import { getRealmDetails } from '../../store/realmSettings/realmSettings';
import { useHistory } from 'react-router-dom';
import { Server } from '@jda/lui-common-icon-library-mui5';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';

const Machines = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const selectedRealmInfo = useSelector(selectRealmInfo);
  const [clients, setClients] = useState<Array<IClient>>([]);
  const [filteredClients, setFilteredClients] = useState<Array<IClient>>([]);
  const [openModal, openCloseModal] = React.useState(false);
  const selectedItem = useSelector(selectMachineCurrentSelection);
  const [showLoader, showHideLoader] = useState(true);
  const [empty, setEmpty] = useState(Boolean);
  const [showDialogLoader, setDialogHideLoader] = useState(false);
  const [isPreviewShown, setPreviewShown] = useState(false);
  const [isDeleteShown, setDeleteShown] = useState(false);
  const machineName = useSelector(selectMachineCurrentName);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const [isUberRealmChild, setIsUberRealmChild] = useState(Boolean);
  const efsFFEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.efsMachineFF);
  const portalV2 = isPortalV2();

  const searchValue = useSelector(selectMachineSearch);
  const writeMachinePermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.machine.read_write], assignedPermissions.permissions);

  const getDefaultRealm = (clientId: string) => {
    if (clientId !== '') {
      getAllowedRealms(clientId).then(async (response: any) => {
        const userRealmId = await getUserTokenRealmId();
        const res = userRealmId !== response?.defaultRealm.id;
        setIsUberRealmChild(res);
      });
    }
  };

  const deleteMachineOnClick = (listItem: IClient) => {
    dispatch(setCurrentMachineSelection(listItem));
    setPreviewShown(true);
  };
  const deleteClient = async () => {
    setDialogHideLoader(true);
    try {
      await deleteClientService(selectedItem!.id)
        .then(() => {
          setDialogHideLoader(false);
          getClients('');
          setPreviewShown(false);
          dispatch(setMachineSearchValue(''));
          dispatch(
            setSnackBarMessage({
              ...SNACKBAR_ALERT_DEFAULT_PROPS,
              message: `Machine ${selectedItem?.displayName} has been deleted successfully`
            })
          );
        })
        .catch(error => {
          setDialogHideLoader(false);
          setPreviewShown(false);
          dispatch(
            setSnackBarMessage({
              ...SNACKBAR_ALERT_DEFAULT_PROPS,
              message: 'Error in deleting machine:' + error.message,
              severity: 'error'
            })
          );
        });
    } catch (err: any) {
      setDialogHideLoader(false);
      setPreviewShown(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: 'Error in deleting machine:' + err.message,
          severity: 'error'
        })
      );
    }
  };

  const onSearchTextBoxChange = (searchValue: string) => {
    dispatch(setMachineSearchValue(searchValue));
  };

  const setCurrentSelection = (response: IClient) => {
    if (selectedItem.id === '') dispatch(setCurrentMachineSelection(response));
  };

  const setInitialMachineName = (response: string) => {
    if (machineName === '') dispatch(setCurrentMachineName(response));
  };

  const getClients = useCallback(
    (clientId: string) => {

      getAllClients()
        .then((responseData: any) => {
          let response = [...responseData.data];
          response.map((item: IClient) => (item.priority = Math.floor(Math.random() * 5)));
          response = response.sort((a, b) => a.displayName.localeCompare(b.displayName));
          if (selectedRealmInfo.envType !== 'admin' && !efsFFEnabled) {
            response = response.filter((item: IClient) => item.type !== 'EFS' && item.type !== 'efs');
          }
          setClients(response);
          setFilteredClients(response);
          showHideLoader(false);
          if (response.length === 0) {
            setDeleteShown(false);
            setEmpty(true);
            setDeleteShown(false);
          } else {
            if (clientId !== '') {
              const item = response.filter(r => r.id === clientId);
              !portalV2 && getDefaultRealm(item[0]?.clientId);
              if (item[0]) {
                dispatch(setCurrentMachineSelection(item[0]));
                setCurrentSelection(item[0]);
                setDeleteShown(!isUberRealmChild);
              } else {
                dispatch(setCurrentMachineSelection(response[0]));
                setCurrentSelection(response[0]);
                setDeleteShown(!isUberRealmChild);
              }
            } else {
              !portalV2 && getDefaultRealm(response[0].clientId);
              setCurrentSelection(response[0]);
              if (response[0].displayName !== undefined) {
                setDeleteShown(!isUberRealmChild);
              }
            }

            setEmpty(false);
          }
        })
        .catch((error: any) => {
          const errorMessage = error.response ? error.response.data.message : error.message;
          dispatch(setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: interpolateString([errorMessage], GET_MACHINE_ERROR),
            severity: 'error'
          }));
          showHideLoader(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const filterBySearchValue = useCallback(
    (searchValue: string) => {
      const clientCopy = [...clients];
      setDeleteShown(true);
      if (searchValue !== '') {
        const searchValueUpperCase = searchValue.toUpperCase();
        const filteredClients = clientCopy.filter(
          client =>
            (client.displayName && client.displayName.toUpperCase().includes(searchValueUpperCase)) ||
            (client.clientId && client.clientId.toUpperCase().includes(searchValueUpperCase)) ||
            (client.subjectId && client.subjectId.toUpperCase().includes(searchValueUpperCase)) ||
            (client.type && client.type.toUpperCase().includes(searchValueUpperCase)) ||
            (client.isExternal !== undefined &&
              (client.isExternal ? 'EXTERNAL' : 'INTERNAL').includes(searchValueUpperCase))
        );
        setFilteredClients(filteredClients);
        setCurrentSelection(filteredClients[0]);
        if (filteredClients.length === 0) {          
          setDeleteShown(false);
        } else {
          setDeleteShown(!isUberRealmChild);
          if (filteredClients[0].displayName !== undefined) {
            setInitialMachineName(filteredClients[0].displayName);
          }
        }
      } else {
        setFilteredClients(clients);
        setCurrentSelection(clients[0]);
        setInitialMachineName(clients[0].displayName)
        setDeleteShown(!isUberRealmChild);
      }
      showHideLoader(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients]
  );

  useEffect(() => {
    getClients(selectedItem.id);
    getRealmDetails(dispatch);
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        showSnackbar: false
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, getClients]);

  useEffect(() => {
    if (clients.length) {
      filterBySearchValue(searchValue);
    }
  }, [searchValue, clients, filterBySearchValue]);

  const handleListItemClick = (listItem: IClient) => {
    if (history.location.search) {
      history.push(`/machineDetail/${listItem.id}${history.location.search}`);
    } else {
      history.push(`/machineDetail/${listItem.id}`);
    }
    dispatch(setCurrentMachineSelection(listItem));
    setDeleteShown(!listItem.isUberRealmClient);
    if (listItem.displayName !== undefined) {
      dispatch(setCurrentMachineName(listItem.displayName));
      !portalV2 && getDefaultRealm(listItem.clientId);
    }
    dispatch(setMachineSelectedTab(null));
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        autoHideDuration: 5000,
        showSnackbar: true,
        message: message,
        severity: severity
      })
    );
  };

  const snackbarSetHidden = () => {
    dispatch(hideSnackbar());
  };

  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Box className={classes.searchBarWrap}>
          <CustomSearch
            value={searchValue}
            placeHolder={SEARCH_MACHINE}
            onSearchChange={onSearchTextBoxChange}
            onClearInput={() => onSearchTextBoxChange('')}
          />
        </Box>
      )
    };
    const rightComponents = {
      component: (
        <Box>
          <CheckViewPermission permission={[permissions.machine.read_write]}>
            <Button
              id="add-machine-button"
              variant="contained"
              size='small'
              color="primary"
              disableElevation
              startIcon={<Server />}
              onClick={() => openCloseModal(!openModal)}
              data-testid="add-machine-button"
            >
              {ADD_MACHINE}
            </Button>
          </CheckViewPermission>
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };
  const emptyTableFallback = () => {
        return (
          <Box my={5}>
            <LuiEmptyState
              imageItem={null}
              heading={NO_RESULT_FOUND}
              subHeading={PLEASE_TRY_A_DIFFERENT_MACHINE}
            />
          </Box>
        );
      };
  return (
    <CheckViewPermission permission={[permissions.machine.read, permissions.machine.read_write]}>
      {empty && (
        <Grid container className={classes.emptyScreenRoot}>
          <Grid container>
            <Grid item xs={12} sm={12}>
              <EmptyComponent
                primaryButtonText={EMPTY_SCREEN_TEXT}
                title={EMPTY_SCREEN_TITLE}
                disableCreateAction={!writeMachinePermission}
                handleOnCreateAction={() => {
                  openCloseModal(!openModal);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      <Box className={empty ? classes.hide : classes.root}>
        <Grid container className={classes.mainContent}>
          <Grid item xs={12} sm={12}>
              <MachineList
                loading={showLoader}
                filteredClients={filteredClients}
                selectedItem={selectedItem}
                deleteOnClick={deleteMachineOnClick}
                isUberRealmChild={isUberRealmChild}
                handleListItemClick={handleListItemClick}
                extendedToolbar={extendedToolbar}
                renderEmptyState={emptyTableFallback}
              />
              {isPreviewShown && (
                <AlertDialog
                  dialogTitle={DELETE_MACHINE_TITLE}
                  dialogContentText={DIALOGUE_CONTENT}
                  recordName={machineName}
                  primaryButtonText={DIALOGUE_OK}
                  primaryButtonColor='error'
                  secondButtonText={DIALOGUE_CANCEL}
                  handleOnCancel={() => setPreviewShown(false)}
                  handleOnOk={deleteClient}
                  showDialogLoader={showDialogLoader}
                />
              )}
          </Grid>
        </Grid>
        {openModal && (
          <AddMachineDialog
            openModal={openModal}
            openCloseModal={openCloseModal}
            hideSnackbar={snackbarSetHidden}
            displaySnackbar={displaySnackbar}
            refreshOnSave={(machineId, secretTextValue) => {
              getClients(machineId);
              if (history.location.search) {
                history.push(`/machineDetail/${machineId}${history.location.search}`, { secretText: secretTextValue });
              } else {
                history.push(`/machineDetail/${machineId}`, { secretText: secretTextValue });
              }
            }}
          />
        )}
      </Box>
      <SnackbarAlert />
    </CheckViewPermission>
  );
};

const Machine = {
  component: Machines,
  name: 'Machines'
};

export default Machine;
